<template>
  <div class="card">
    <div class="card-body">
      <h4 class="card-title">Orderbevestigingen</h4>
      <div class="row p-0 m-0">
        <div class="col-lg-3 col-md-4 mt-2">
          <FilterOrderBevestiging 
            @filterStatusChanged="filterStatusChanged" 
            :orderBevestigingStatussen="orderConfirmationStatussen"
          />
        </div>
        <div class="col-lg-9 col-md-8">
          <SearchOrder
            :searchQuery="searchQuery"
            placeholder="Zoeken op klantnaam, referentie of VO-nummer"
            @performSearch="performSearch"
          />
          <div class="table-responsive">
            <table class="table caption-top table-striped border table-hover">
              <caption>Lijst met orderbevestigingen</caption>
              <thead>
                <tr>
                  <th scope="col" @click="sort('status')" :class="sortClass('status')">&nbsp;</th>
                  <th scope="col" @click="sort('created_at')" :class="sortClass('created_at')" class="text-center">Aangemaakt</th>
                  <th scope="col" @click="sort('customer')" :class="sortClass('customer')">Klant</th>
                  <th scope="col" @click="sort('reference')" :class="sortClass('reference')">Referentie</th>
                  <th scope="col" @click="sort('vo_number')" :class="sortClass('vo_number')">VO-nummer</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(orderConfirmation, index) in filteredOrderConfirmations" :key="index" class="clickable-row"
                    @click="goToOrderConfirmationDetailPage(index)">
                  <td><i class="bi bi-circle-fill close" :class="getColor(orderConfirmation.status_id)" :title="getStatusName(orderConfirmation.status_id)"></i></td>
                  <td class="text-center">{{ getDateTime(orderConfirmation.created_at) }}</td>
                  <td>{{ orderConfirmation.customer_name }}</td>
                  <td>{{ orderConfirmation.reference }}</td>
                  <td>{{ orderConfirmation.vo_number }}</td>
                </tr>
              </tbody>
            </table>
            <div ref="sentinel"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ServiceFactory from "../services/ServiceFactory"
import FilterOrderBevestiging from "../components/FilterOrderBevestiging.vue"
import Utils from "../utils/Utils"
import SearchOrder from "@/components/SearchOrder.vue"
import FetchOrderConfirmationsAction from "@/services/FetchOrderConfirmationsAction.js"
import NavigationService from "@/services/NavigationService.js"

export default {
  name: 'OrderBevestigingenPage',
  props: ['user'],
  components: { SearchOrder, FilterOrderBevestiging },
  data() {
    return {
      orderConfirmations: [],
      orderConfirmationStatussen: [],
      filteredOrderConfirmations: [],
      currentSort: 'updated_at',
      currentSortDir: 'desc',
      searchQuery: '',
      searchReference: '',
      searchStatus: '',
      webSocketService: null,
      page: 1,
      prevPage: 0,
      pageSize: 25,
      observer: null,
      currentFilter: null
    }
  },
  watch: {
    user: {
      handler: function (val) {
        if (val) {
          this.loadOrderConfirmations()
          this.loadOrderConfirmationStatuses()
        }
      },
      deep: true
    }
  },
  methods: {
    performSearch(query) {
      this.searchQuery = query;
      this.page = 1;
      this.prevPage = 0;
      this.loadOrderConfirmationsForSearch();
      this.prevPage = this.page;
      this.page += 1;
    },
    async loadOrderConfirmationsForSearch() {
      const newOrderConfirmations = await this.loadOrderConfirmationList();
      if (!newOrderConfirmations) return;
      this.orderConfirmations = newOrderConfirmations;
      this.filteredOrderConfirmations = newOrderConfirmations;
      
      // Sla de gefilterde orderbevestigingen op voor navigatie
      if (NavigationService) {
        NavigationService.saveItems(this.filteredOrderConfirmations, {
          searchQuery: this.searchQuery,
          currentSort: this.currentSort,
          currentSortDir: this.currentSortDir,
          searchStatus: this.searchStatus,
          stateFilter: this.stateFilter,
          searchDataStart: this.searchDataStart,
          searchDataEnd: this.searchDataEnd,
          searchCustomers: this.searchCustomers,
          searchUserAgent: this.searchUserAgent
        });
      }
    },
    async loadOrderConfirmations() {
      //voorkom dubbel laden van dezelfde pagina
      if (this.prevPage === this.page) return;
      this.prevPage = this.page;

      const newOrderConfirmations = await this.loadOrderConfirmationList();
      if (!newOrderConfirmations) return;

      this.orderConfirmations = [...this.orderConfirmations, ...newOrderConfirmations];
      this.filteredOrderConfirmations = this.orderConfirmations;
      this.page += 1;
    },
    async loadOrderConfirmationList() {
      try {
        const result = await FetchOrderConfirmationsAction.getOrderConfirmations(
          this.page,
          this.pageSize,
          this.currentSort,
          this.currentSortDir.toUpperCase(),
          this.searchQuery,
          this.searchStatus,
          this.searchReference
        );
        if (!result) return [];
        
        return [...result];
      } catch (error) {
        console.error('Fout bij ophalen van orderbevestigingen:', error);
        this.$emit('showToast', ['Fout bij ophalen van orderbevestigingen', 'bg-danger']);
        return [];
      }
    },
    getColor(statusId) {
      // Implementeer kleurcodering op basis van status ID
      return Utils.getColorForOrderConfirmationStatus(statusId);
    },
    getStatusName(statusId) {
      const status = this.orderConfirmationStatussen.find(s => s.id === statusId);
      return status ? status.label : 'Onbekend';
    },
    sort(sortBy) {
      // Als op hetzelfde veld wordt geklikt, verander dan de sorteerrichting
      if (sortBy === this.currentSort) {
        this.currentSortDir = this.currentSortDir === 'desc' ? 'asc' : 'desc';
      }
      this.currentSort = sortBy;
      
      // Reset en herlaad orderbevestigingen met nieuwe sortering
      this.page = 1;
      this.prevPage = 0;
      this.orderConfirmations = [];
      this.loadOrderConfirmations();
    },
    sortClass(column) {
      if (column === this.currentSort) {
        return this.currentSortDir === 'desc' ? 'sort-desc' : 'sort-asc';
      }
      return '';
    },
    filterStatusChanged(filterObj) {
      let statuses = [];
      if (filterObj.status && typeof filterObj.status === 'object' && !Array.isArray(filterObj.status)) {
        statuses = Object.entries(filterObj.status)
            .filter(([, value]) => value === true)
            .map(([key]) => Number(key));
      }
      this.searchStatus = statuses.join(',');
      this.currentFilter = filterObj;
      
      // Reset paginering en zoek opnieuw met nieuwe filters
      this.page = 1;
      this.prevPage = 0;
      this.performSearch(this.searchQuery);
    },
    allFiltersUnchecked(type) {
      for (let i in type) {
        if (type[i]) {
          return false;
        }
      }
      return true;
    },
    getDateTime(isoString) {
      return Utils.formatDateTimeString(isoString);
    },
    async loadOrderConfirmationStatuses() {
      try {
        const statussen = await FetchOrderConfirmationsAction.getOrderConfirmationStatuses();
        this.orderConfirmationStatussen = statussen;
        return statussen;
      } catch (error) {
        console.error('Fout bij ophalen van orderbevestigingsstatussen:', error);
        this.$emit('showToast', ['Fout bij ophalen van orderbevestigingsstatussen', 'bg-danger']);
        return [];
      }
    },
    goToOrderConfirmationDetailPage(index) {
      // Sla huidige lijst en filters op voor navigatie terug
      NavigationService.saveItems(
        this.filteredOrderConfirmations,
        {
          searchQuery: this.searchQuery,
          currentSort: this.currentSort,
          currentSortDir: this.currentSortDir,
          searchReference: this.searchReference,
          searchStatus: this.searchStatus
        },
        'orderConfirmation',
        '/order-bevestigingen'
      );
      
      NavigationService.setCurrentIndex(index, 'orderConfirmation');
      
      // Navigeer naar de detail pagina
      this.$router.push({name: 'OrderBevestigingDetail', params: {id: this.filteredOrderConfirmations[index].id}});
    },
    observeSentinel() {
      if (!this.$refs.sentinel) {
        console.log('Sentinel element niet gevonden, observer niet geïnitialiseerd');
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadOrderConfirmations();
          }
        });
      }, options);

      this.observer.observe(this.$refs.sentinel);
    }
  },
  async mounted() {
    if (this.user) {
      // Zorgen dat eventuele cache wordt geleegd bij (her)laden van de pagina
      if (FetchOrderConfirmationsAction) {
        FetchOrderConfirmationsAction.clearCache();
      }
      
      //await this.loadOrderConfirmations();
      await this.loadOrderConfirmationStatuses();
      
      this.webSocketService = ServiceFactory.getService('WebSocketService');
      
      this.webSocketService.addEventListener('orderConfirmationsUpdated', (event) => {
        // Nieuwe orderbevestigingen ontvangen via WebSocket
        this.orderConfirmations = event.detail;
        
        // Cache leegmaken zodat bij terugkeer naar OrderBevestigingenPage de nieuwe orderbevestigingen worden opgehaald
        if (FetchOrderConfirmationsAction) {
          FetchOrderConfirmationsAction.clearCache();
        }
        
        // Filters correct toepassen op de nieuwe data
        this.$nextTick(() => {
          if (this.currentFilter) {
            this.filterStatusChanged(this.currentFilter);
          } else {
            this.filteredOrderConfirmations = this.orderConfirmations;
          }
        });
      });
      this.webSocketService.connect();
    }
    
    this.$nextTick(() => {
      this.observeSentinel();
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.webSocketService) {
      // Verwijder eventuele WebSocket event listeners
    }
  }
}
</script>

<style scoped>
.card {
  border: none;
}

th {
  cursor: pointer;
}

.sort-asc::after {
  content: ' ▲';
}

.sort-desc::after {
  content: ' ▼';
}

.clickable-row {
  cursor: pointer;
}
</style>
